import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as MatchedIcon,
  Warning as S3OnlyIcon,
  Error as RangeOSOnlyIcon,
  CompareArrows as CompareIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { assetValidationApi, ValidationResult, AssetTypeComparison, S3Structure } from '../services/asset-validation-api';
import { rangeosApiService, RangeOSApiEndpoint } from '../services/rangeos-api';

function formatFileSize(bytes: number): string {
  if (!bytes) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
}

function StatusChip({ status }: { status: string }) {
  switch (status) {
    case 'matched':
      return <Chip icon={<MatchedIcon />} label="Matched" size="small" color="success" variant="outlined" />;
    case 'in_s3_only':
      return <Chip icon={<S3OnlyIcon />} label="S3 Only" size="small" color="warning" variant="outlined" />;
    case 'in_rangeos_only':
      return <Chip icon={<RangeOSOnlyIcon />} label="RangeOS Only" size="small" color="error" variant="outlined" />;
    default:
      return null;
  }
}

export const AssetValidationPanel: React.FC = () => {
  const [endpoints, setEndpoints] = useState<RangeOSApiEndpoint[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [s3Structure, setS3Structure] = useState<S3Structure | null>(null);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [selectedProject, setSelectedProject] = useState('base-templates');
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [structureLoading, setStructureLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load endpoints and S3 structure on mount
  useEffect(() => {
    const init = async () => {
      try {
        const eps = await rangeosApiService.getEndpoints();
        setEndpoints(eps);
        if (eps.length > 0) {
          setSelectedEndpoint(eps[0].id);
        }
      } catch (err: any) {
        // Endpoints may not be configured yet
      }

      setStructureLoading(true);
      try {
        const structure = await assetValidationApi.discoverS3Structure();
        setS3Structure(structure);
        if (structure.branches.includes('main')) {
          setSelectedBranch('main');
        } else if (structure.branches.length > 0) {
          setSelectedBranch(structure.branches[0]);
        }
      } catch (err: any) {
        // S3 structure may not be available
      } finally {
        setStructureLoading(false);
      }
    };

    init();
  }, []);

  // Update available projects when branch changes
  const availableProjects = s3Structure?.projects[selectedBranch] || [];

  useEffect(() => {
    if (availableProjects.length > 0 && !availableProjects.includes(selectedProject)) {
      setSelectedProject(availableProjects[0]);
    }
  }, [selectedBranch, availableProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValidate = async () => {
    if (!selectedEndpoint) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const validationResult = await assetValidationApi.validate(
        selectedEndpoint,
        selectedBranch,
        selectedProject,
      );
      setResult(validationResult);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderAssetTypeSection = (comparison: AssetTypeComparison) => {
    const hasIssues = comparison.inS3Only > 0 || comparison.inRangeOSOnly > 0;

    return (
      <Accordion key={comparison.assetType} defaultExpanded={hasIssues}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 160 }}>
              {comparison.label}
            </Typography>
            <Chip
              label={`${comparison.matched} matched`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ display: comparison.matched > 0 ? 'inline-flex' : 'none' }}
            />
            <Chip
              label={`${comparison.inS3Only} S3 only`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ display: comparison.inS3Only > 0 ? 'inline-flex' : 'none' }}
            />
            <Chip
              label={`${comparison.inRangeOSOnly} RangeOS only`}
              size="small"
              color="error"
              variant="outlined"
              sx={{ display: comparison.inRangeOSOnly > 0 ? 'inline-flex' : 'none' }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              S3: {comparison.s3Count} | RangeOS: {comparison.rangeosCount}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {comparison.items.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No assets found in either S3 or RangeOS for this type.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 120 }}>Status</TableCell>
                    <TableCell sx={{ width: 100 }} align="right">S3 Size</TableCell>
                    <TableCell sx={{ width: 200 }}>RangeOS UUID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparison.items.map((item, idx) => (
                    <TableRow
                      key={`${item.name}-${idx}`}
                      sx={{
                        bgcolor: item.status === 'in_s3_only'
                          ? 'warning.50'
                          : item.status === 'in_rangeos_only'
                            ? 'error.50'
                            : undefined,
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={item.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                          {item.s3Size ? formatFileSize(item.s3Size) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.rangeosUuid ? (
                          <Tooltip title={item.rangeosUuid}>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" noWrap>
                              {item.rangeosUuid.substring(0, 12)}...
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CompareIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          Asset Validation
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compare assets in S3 against what's registered in a RangeOS endpoint.
        Identifies missing assets (in S3 but not registered) and extra assets (registered but not in S3).
      </Typography>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>RangeOS Endpoint</InputLabel>
            <Select
              value={selectedEndpoint}
              onChange={(e) => setSelectedEndpoint(e.target.value)}
              label="RangeOS Endpoint"
            >
              {endpoints.map(ep => (
                <MenuItem key={ep.id} value={ep.id}>{ep.name}</MenuItem>
              ))}
              {endpoints.length === 0 && (
                <MenuItem disabled value="">No endpoints configured</MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>S3 Branch</InputLabel>
            <Select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              label="S3 Branch"
              disabled={structureLoading}
            >
              {(s3Structure?.branches || ['main', 'develop']).map(b => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>S3 Project</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="S3 Project"
              disabled={structureLoading}
            >
              {(availableProjects.length > 0 ? availableProjects : ['base-templates']).map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <CompareIcon />}
            onClick={handleValidate}
            disabled={loading || !selectedEndpoint}
          >
            {loading ? 'Validating...' : 'Validate'}
          </Button>
        </Box>

        {result && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Comparing <strong>s3://assets/{result.s3Branch}/</strong> vs <strong>{result.endpointName}</strong>
              {' '} (validated {new Date(result.timestamp).toLocaleTimeString()})
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">{result.summary.totalMatched}</Typography>
              <Typography variant="caption">Matched</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
              <Typography variant="h5" color="warning.main">{result.summary.totalS3Only}</Typography>
              <Typography variant="caption">S3 Only</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
              <Typography variant="h5" color="error.main">{result.summary.totalRangeOSOnly}</Typography>
              <Typography variant="caption">RangeOS Only</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
              <Typography variant="h5">{result.summary.totalS3}</Typography>
              <Typography variant="caption">Total S3</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
              <Typography variant="h5">{result.summary.totalRangeOS}</Typography>
              <Typography variant="caption">Total RangeOS</Typography>
            </Paper>
          </Box>

          {/* Per asset type */}
          {result.assetTypes.map(renderAssetTypeSection)}
        </Box>
      )}

      {/* Empty state */}
      {!result && !loading && !error && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Select an endpoint and click "Validate" to compare S3 assets against RangeOS
          </Typography>
        </Box>
      )}
    </Box>
  );
};
